import {action, computed, makeAutoObservable, observable, runInAction} from "mobx";
import {inject, injectable} from "inversify";
import {filter, find, get, sortBy} from "lodash";
import {ContestUtils} from "data/utils/contest_utils";
import {Bindings} from "data/constants/bindings";
import type {IJSONProvider} from "data/providers/json/json.provider";
import {IAnswer, IContest, IQuestionWithAnswers} from "data/types/contests";
import {
	type IGameplayApiProvider,
	ISaveRequests,
	IGetRequests,
} from "data/providers/api/gameplay.api.provider";

export interface IGameplayStore {
	get allContests(): IContest[];
	get scheduledContests(): IContest[];
	get actualContests(): IContest[];
	get activeContests(): IContest[];
	get pastContests(): IContest[];
	get currentContests(): IContest[];
	get upcomingContests(): IContest[];

	getQuestionsByContestID(contestID: number): IQuestionWithAnswers[];
	getCorrectAnswersByContestID(contestID: number): IAnswer[];

	requestContests(): Promise<IContest[]>;
	requestContestsSafety(): Promise<IContest[]>;
	setContestComplete(contestID: number): void;
	saveAnswers(
		params: ISaveRequests["variables"] & {contestID: number}
	): Promise<ISaveRequests["response"]>;
	requestData(params: IGetRequests["variables"]): Promise<void>;
}

@injectable()
export class GameplayStore implements IGameplayStore {
	@observable _contests: IContest[] = [];
	@observable _answers: Record<string, IAnswer[]> = {};

	get allContests() {
		return sortBy(this._contests, ContestUtils.sortByStatus);
	}

	@computed get scheduledContests() {
		return filter(this._contests, ContestUtils.isScheduled);
	}

	@computed get actualContests() {
		return filter(this._contests, ContestUtils.isActual);
	}

	@computed get activeContests() {
		return filter(this._contests, ContestUtils.isActive);
	}

	@computed get pastContests() {
		return filter(this._contests, ContestUtils.isPast);
	}

	@computed get currentContests() {
		return sortBy(filter(this._contests, ContestUtils.isCurrent), ContestUtils.sortByStatus);
	}

	@computed get upcomingContests() {
		return filter(this._contests, ContestUtils.isUpcoming);
	}

	@computed getQuestionsByContestID(contestID: number) {
		const questions =
			this.allContests.find((contest) => contest.id === contestID)?.questions || [];

		return questions.map((question) => ({
			...question,
			answers:
				this._answers[contestID]?.filter((answer) => answer.questionId === question.id) ??
				[],
		}));
	}

	@computed getCorrectAnswersByContestID(contestID: number) {
		return this._answers[contestID]?.filter((it) => it.isCorrect) || [];
	}

	constructor(
		@inject(Bindings.GameplayApiProvider) private _gameplayApi: IGameplayApiProvider,
		@inject(Bindings.JSONProvider) private _jsonProvider: IJSONProvider
	) {
		makeAutoObservable(this);
	}

	@action setContestComplete(contestID: number): void {
		const contest = find(this.allContests, {id: contestID})!;
		contest.status = ContestUtils.status.COMPLETED;
	}

	@action async requestContests() {
		const resp = await this._jsonProvider.contests();
		const contests = resp.data.contests;
		runInAction(() => (this._contests = contests));
		return contests;
	}

	@action
	async requestContestsSafety() {
		if (this._contests.length) {
			return Promise.resolve(this._contests);
		}

		return this.requestContests();
	}

	async saveAnswers({contestID, ...params}: ISaveRequests["variables"] & {contestID: number}) {
		const oldAnswers = get(this._answers, contestID, []);

		this._answers[contestID] = [
			...oldAnswers.filter(({questionId}) => questionId !== params.questionId),
			{questionId: params.questionId, optionId: params.optionId, isCorrect: null},
		];

		const response = await this._gameplayApi.saveAnswers(params);
		return response.data;
	}

	@action
	async requestData(params: IGetRequests["variables"]) {
		const response = await this._gameplayApi.getAnswers(params);

		runInAction(() => {
			this._answers[params.contestId] = response.data.success.answers;
		});
	}
}
